import { useEffect, useState } from 'react'
import { Text, View } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import type { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { reportUnexpectedError } from '../../lib/reportError'
import type { AdminAuthStackParamList } from '../../navigation/types'
import { getActiveAdminAccount } from './auth/adminAccount'
import type { AdminAccount } from './auth/adminAccount'

interface AdminProtectedRouteProps {
  children: (adminAccount: AdminAccount) => React.ReactNode
}

export function AdminProtectedRoute({ children }: AdminProtectedRouteProps) {
  const navigation = useNavigation<NativeStackNavigationProp<AdminAuthStackParamList>>()
  const { t } = useTranslation()
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [adminAccount, setAdminAccount] = useState<AdminAccount | null>(null)

  useEffect(() => {
    let isMounted = true

    supabase.auth.getSession()
      .then(async ({ data: { session } }) => {
        if (!session?.user) return null
        return getActiveAdminAccount(session.user.id)
      })
      .then((nextAdminAccount) => {
        if (!isMounted) return
        if (!nextAdminAccount) {
          navigation.replace('SignIn')
          return
        }
        setAdminAccount(nextAdminAccount)
        setIsCheckingAuth(false)
      })
      .catch((err) => {
        // getActiveAdminAccount already swallows its own query errors to
        // null (Supabase-visible-skip) — anything reaching here is
        // getSession() itself failing, a local session-storage read
        // failure with no backend round trip, invisible anywhere else.
        reportUnexpectedError(err)
        if (isMounted) {
          navigation.replace('SignIn')
        }
      })

    return () => {
      isMounted = false
    }
  }, [navigation])

  if (isCheckingAuth || !adminAccount) {
    return (
      <View className="flex-1 bg-canvas-primary px-8 py-8">
        <Text className="text-base font-normal text-secondary">{t('common.loading')}</Text>
      </View>
    )
  }

  return <>{children(adminAccount)}</>
}
